import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, WheelEvent as ReactWheelEvent } from 'react';
import type { Room } from 'livekit-client';
import { logInfo } from '@/lib/logger';

type WebRTCStatus = 'idle' | 'connecting' | 'connected' | 'failed';

type CameraSwitchDirection = 'prev' | 'next';

type TeleopCameraConfig = {
  defaultTrack: string;
  secondaryTrack: string;
  swipeThreshold: number;
  swipeRatio: number;
  wheelThreshold: number;
  swipeCooldownMs: number;
};

type UseTeleopCameraSessionParams = {
  roomId: string;
  robotId: string;
  debugWs: boolean;
  config: TeleopCameraConfig;
};

type UseTeleopCameraSessionResult = {
  livekitRoom: string;
  livekitRoomInstance: Room | null;
  webrtcStatus: WebRTCStatus;
  activeCamera: string;
  canSwitchCamera: boolean;
  switchToSecondary: () => void;
  switchToOverhead: () => void;
  triggerCameraSwitch: (direction: CameraSwitchDirection) => void;
  handleTracksChange: (tracks: string[]) => void;
  handleActiveTrackChange: (trackName: string) => void;
  handleLivekitRoom: (room: Room | null) => void;
  handleWebrtcStatusChange: (status: WebRTCStatus) => void;
  handleVideoPointerDown: (event: ReactPointerEvent<HTMLVideoElement>) => void;
  handleVideoPointerUp: (event: ReactPointerEvent<HTMLVideoElement>) => void;
  handleVideoPointerCancel: (event: ReactPointerEvent<HTMLVideoElement>) => void;
  handleVideoWheel: (event: ReactWheelEvent<HTMLVideoElement>) => void;
};

const isEditableTarget = (target: EventTarget | null) => {
  const isHtmlElement = target instanceof HTMLElement;

  if (!isHtmlElement) {
    return false;
  }

  if (target.isContentEditable) {
    return true;
  }

  const tagName = target.tagName.toLowerCase();
  const isEditableTag =
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select';

  return isEditableTag;
};

export const useTeleopCameraSession = ({
  roomId,
  robotId,
  debugWs,
  config,
}: UseTeleopCameraSessionParams): UseTeleopCameraSessionResult => {
  const [webrtcStatus, setWebrtcStatus] = useState<WebRTCStatus>('idle');
  const [cameraTracks, setCameraTracks] = useState<string[]>([]);
  const [activeCamera, setActiveCamera] = useState(config.defaultTrack);
  const [livekitRoomInstance, setLivekitRoomInstance] = useState<Room | null>(null);
  const swipeRef = useRef<{ x: number; y: number; id: number | null }>({
    x: 0,
    y: 0,
    id: null,
  });
  const lastSwipeAtRef = useRef(0);

  const livekitRoom = useMemo(() => {
    const nextLivekitRoom = roomId || robotId;

    return nextLivekitRoom;
  }, [robotId, roomId]);
  const canSwitchCamera = cameraTracks.length > 1;
  const hasSecondaryTrack = cameraTracks.includes(config.secondaryTrack);

  const cycleCamera = useCallback(
    (direction: CameraSwitchDirection) => {
      const hasTracks = cameraTracks.length > 0;

      if (!hasTracks) {
        return;
      }

      const currentIndex = cameraTracks.indexOf(activeCamera);
      const normalizedIndex = currentIndex === -1 ? 0 : currentIndex;
      const nextIndex =
        direction === 'next'
          ? (normalizedIndex + 1) % cameraTracks.length
          : (normalizedIndex - 1 + cameraTracks.length) % cameraTracks.length;
      const nextTrack = cameraTracks[nextIndex];

      setActiveCamera(nextTrack);
    },
    [activeCamera, cameraTracks]
  );

  const switchToSecondary = useCallback(() => {
    if (!hasSecondaryTrack) {
      if (debugWs) {
        logInfo(
          'teleop.track.secondary.unavailable',
          'secondary track unavailable',
        );
      }

      return;
    }

    if (activeCamera !== config.secondaryTrack) {
      setActiveCamera(config.secondaryTrack);
    }
  }, [activeCamera, config.secondaryTrack, debugWs, hasSecondaryTrack]);

  const switchToOverhead = useCallback(() => {
    setActiveCamera((currentTrack) => {
      const isDefaultTrack = currentTrack === config.defaultTrack;

      if (isDefaultTrack) {
        return currentTrack;
      }

      return config.defaultTrack;
    });
  }, [config.defaultTrack]);

  const triggerCameraSwitch = useCallback(
    (direction: CameraSwitchDirection) => {
      if (!canSwitchCamera) {
        return;
      }

      const now = Date.now();
      const millisecondsSinceLastSwipe = now - lastSwipeAtRef.current;
      const isInCooldown =
        millisecondsSinceLastSwipe < config.swipeCooldownMs;

      if (isInCooldown) {
        return;
      }

      lastSwipeAtRef.current = now;
      cycleCamera(direction);
    },
    [canSwitchCamera, config.swipeCooldownMs, cycleCamera]
  );

  const handleTracksChange = useCallback((tracks: string[]) => {
    setCameraTracks(tracks);
  }, []);

  const handleActiveTrackChange = useCallback((trackName: string) => {
    setActiveCamera(trackName);
  }, []);

  const handleLivekitRoom = useCallback((room: Room | null) => {
    setLivekitRoomInstance(room);
  }, []);

  const handleWebrtcStatusChange = useCallback((status: WebRTCStatus) => {
    setWebrtcStatus(status);
  }, []);

  const handleVideoPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLVideoElement>) => {
      if (!canSwitchCamera) {
        return;
      }

      const isMousePointer = event.pointerType === 'mouse';
      const isPrimaryButton = event.button === 0;

      if (isMousePointer && !isPrimaryButton) {
        return;
      }

      swipeRef.current = {
        x: event.clientX,
        y: event.clientY,
        id: event.pointerId,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [canSwitchCamera]
  );

  const handleVideoPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLVideoElement>) => {
      if (!canSwitchCamera) {
        return;
      }

      const activeSwipeId = swipeRef.current.id;
      const isActiveSwipePointer = activeSwipeId === event.pointerId;

      if (!isActiveSwipePointer) {
        return;
      }

      const deltaX = event.clientX - swipeRef.current.x;
      const deltaY = event.clientY - swipeRef.current.y;
      const isHorizontalSwipe =
        Math.abs(deltaX) > config.swipeThreshold &&
        Math.abs(deltaX) > Math.abs(deltaY) * config.swipeRatio;

      swipeRef.current.id = null;

      if (!isHorizontalSwipe) {
        return;
      }

      const nextDirection = deltaX < 0 ? 'next' : 'prev';

      triggerCameraSwitch(nextDirection);
    },
    [
      canSwitchCamera,
      config.swipeRatio,
      config.swipeThreshold,
      triggerCameraSwitch,
    ]
  );

  const handleVideoPointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLVideoElement>) => {
      const activeSwipeId = swipeRef.current.id;
      const isActiveSwipePointer = activeSwipeId === event.pointerId;

      if (isActiveSwipePointer) {
        swipeRef.current.id = null;
      }
    },
    []
  );

  const handleVideoWheel = useCallback(
    (event: ReactWheelEvent<HTMLVideoElement>) => {
      if (!canSwitchCamera) {
        return;
      }

      const exceedsDeltaThreshold =
        Math.abs(event.deltaX) >= config.wheelThreshold;
      const isHorizontalDominant =
        Math.abs(event.deltaX) >= Math.abs(event.deltaY);

      if (!exceedsDeltaThreshold || !isHorizontalDominant) {
        return;
      }

      event.preventDefault();
      const nextDirection = event.deltaX > 0 ? 'next' : 'prev';

      triggerCameraSwitch(nextDirection);
    },
    [canSwitchCamera, config.wheelThreshold, triggerCameraSwitch]
  );

  useEffect(() => {
    const hasTracks = cameraTracks.length > 0;

    if (!hasTracks) {
      return;
    }

    const activeTrackStillExists = cameraTracks.includes(activeCamera);

    if (activeTrackStillExists) {
      return;
    }

    const nextTrack = cameraTracks[0];

    setActiveCamera(nextTrack);
  }, [activeCamera, cameraTracks]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const targetIsEditable = isEditableTarget(event.target);

      if (targetIsEditable) {
        return;
      }

      if (!event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }

      event.preventDefault();
      const nextDirection = event.key === 'ArrowRight' ? 'next' : 'prev';

      triggerCameraSwitch(nextDirection);
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [triggerCameraSwitch]);

  return {
    livekitRoom,
    livekitRoomInstance,
    webrtcStatus,
    activeCamera,
    canSwitchCamera,
    switchToSecondary,
    switchToOverhead,
    triggerCameraSwitch,
    handleTracksChange,
    handleActiveTrackChange,
    handleLivekitRoom,
    handleWebrtcStatusChange,
    handleVideoPointerDown,
    handleVideoPointerUp,
    handleVideoPointerCancel,
    handleVideoWheel,
  };
};
